import { useEffect, useRef, useState } from 'react';
import { useToast } from '../../../hooks/use-toast';
import { RealtimeChat } from '../../../utils/RealtimeAudio';
import { Phone, PhoneOff, X, MessageCircle, Send, Mic } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Toaster } from '../../ui/toaster';
import { supabaseReservation } from '../../../integrations/supabase/client';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

type CommunicationMode = 'voice' | 'chat' | 'both';
type ActiveTab = 'voice' | 'chat';

const EmbedWidget = () => {
  const { toast } = useToast();

  // Detect whether we're running embedded (in an iframe) or standalone (opened directly)
  const hasParent = typeof window !== 'undefined' && window.parent !== window;

  // Get URL params for customization
  const params = new URLSearchParams(window.location.search);
  const mode = params.get('mode') || 'single';
  const isLauncherMode = mode === 'launcher';
  const isPanelMode = mode === 'panel';

  // Token-based auth (no login required)
  const embedToken = params.get('token');
  const accountIdParam = params.get('account_id');

  // In panel mode, default to open (for host-iframe usage). In standalone panel preview we
  // allow closing to show a launcher-like button.
  const [isOpen, setIsOpen] = useState(isPanelMode);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [status, setStatus] = useState<'ready' | 'connecting' | 'listening' | 'speaking'>('ready');
  const [messages, setMessages] = useState<Message[]>([]);
  const [communicationMode, setCommunicationMode] = useState<CommunicationMode>('voice');
  const [activeTab, setActiveTab] = useState<ActiveTab>('voice');
  const [chatInput, setChatInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [tokenValidated, setTokenValidated] = useState(false);
  const [validatedAccountId, setValidatedAccountId] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const chatRef = useRef<RealtimeChat | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const accentColor = params.get('accent') || '#0A84FF';
  const position = params.get('position') || 'bottom-right';
  const titleParam = params.get('title');

  // Use validated account ID, or fall back to URL param for backwards compatibility
  const accountId = validatedAccountId || accountIdParam;

  // Validate embed token on mount (if provided) - also fetches config via service role
  useEffect(() => {
    const validateToken = async () => {
      // If no token is provided, use legacy account_id mode (backwards compatible)
      if (!embedToken) {
        if (accountIdParam) {
          setValidatedAccountId(accountIdParam);
          setTokenValidated(true);
          // Legacy mode: need to fetch config client-side (may fail due to RLS)
          fetchConfigFallback(accountIdParam);
        } else {
          setAuthError('No authentication provided');
        }
        return;
      }

      try {
        const { data, error } = await supabaseReservation.functions.invoke('validate-embed-token', {
          body: { token: embedToken }
        });

        if (error || !data?.valid) {
          console.error('[EmbedWidget] Token validation failed:', error || data?.error);
          setAuthError(data?.error || 'Invalid embed token');
          return;
        }

        setValidatedAccountId(data.account_id);
        setTokenValidated(true);
        
        // Use communication_mode from token validation (fetched with service role - always works)
        if (data.communication_mode) {
          const mode = data.communication_mode as CommunicationMode;
          setCommunicationMode(mode);
          if (mode === 'chat') {
            setActiveTab('chat');
          } else {
            setActiveTab('voice');
          }
        }
        setConfigLoaded(true);
        
        console.log('[EmbedWidget] Token validated for account:', data.account_id, 'mode:', data.communication_mode);
      } catch (err) {
        console.error('[EmbedWidget] Token validation error:', err);
        setAuthError('Failed to validate token');
      }
    };

    validateToken();
  }, [embedToken, accountIdParam]);

  // Fallback config fetch for legacy account_id mode (may fail due to RLS on mobile)
  const fetchConfigFallback = async (accId: string) => {
    try {
      const { data } = await supabaseReservation
        .from('agent_config')
        .select('communication_mode')
        .eq('account_id', accId)
        .limit(1)
        .maybeSingle();

      if (data?.communication_mode) {
        const mode = data.communication_mode as CommunicationMode;
        setCommunicationMode(mode);
        if (mode === 'chat') {
          setActiveTab('chat');
        } else {
          setActiveTab('voice');
        }
      }
    } catch (error) {
      console.error('Error fetching config (fallback):', error);
    } finally {
      setConfigLoaded(true);
    }
  };

  // Set page background to transparent for embedding
  useEffect(() => {
    document.documentElement.style.backgroundColor = 'transparent';
    document.body.style.backgroundColor = 'transparent';
    return () => {
      document.documentElement.style.backgroundColor = '';
      document.body.style.backgroundColor = '';
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const extractAssistantText = (event: any): string | null => {
    const direct = event?.text ?? event?.output_text ?? event?.transcript;
    if (typeof direct === 'string' && direct.trim()) return direct.trim();

    const output = event?.response?.output;
    if (Array.isArray(output)) {
      const parts: string[] = [];
      for (const item of output) {
        const content = item?.content;
        if (!Array.isArray(content)) continue;
        for (const c of content) {
          if (typeof c?.text === 'string') parts.push(c.text);
          if (typeof c?.transcript === 'string') parts.push(c.transcript);
        }
      }
      const joined = parts.join('').trim();
      return joined || null;
    }

    return null;
  };

  const handleMessage = (event: Record<string, unknown>) => {
    const isTextOnlyActive = communicationMode === 'chat' || (communicationMode === 'both' && activeTab === 'chat');

    // Voice-mode events
    if (!isTextOnlyActive) {
      if (event.type === 'response.audio.delta') {
        setIsSpeaking(true);
        setStatus('speaking');
        return;
      }
      if (event.type === 'response.audio.done') {
        setIsSpeaking(false);
        setStatus('listening');
        return;
      }
      if (event.type === 'input_audio_buffer.speech_started') {
        setStatus('listening');
        return;
      }
      if (event.type === 'response.audio_transcript.done') {
        const transcript = (event as { transcript?: string }).transcript;
        if (transcript) {
          setMessages((prev) => [...prev, { role: 'assistant', content: transcript }]);
        }
        return;
      }
      if (event.type === 'conversation.item.input_audio_transcription.completed') {
        const transcript = (event as { transcript?: string }).transcript;
        if (transcript) {
          setMessages((prev) => [...prev, { role: 'user', content: transcript }]);
        }
        return;
      }
    }

    // Text-mode events - only handle response.done for text-only mode to avoid duplicates
    if (isTextOnlyActive && event.type === 'response.done') {
      const text = extractAssistantText(event);
      if (text) {
        setMessages((prev) => [...prev, { role: 'assistant', content: text }]);
      }
      setIsSending(false);
      setStatus('ready');
      return;
    }
  };

  const startConversation = async () => {
    setIsConnecting(true);
    setStatus('connecting');

    // Determine if we should use text-only mode (no microphone)
    const useTextOnly = communicationMode === 'chat' || (communicationMode === 'both' && activeTab === 'chat');

    try {
      chatRef.current = new RealtimeChat(handleMessage, undefined, accountId, useTextOnly);
      await chatRef.current.init();
      setIsConnected(true);
      setStatus(useTextOnly ? 'ready' : 'listening');
      toast({
        title: "Connected",
        description: useTextOnly ? "Type your message" : "Start speaking!",
      });
    } catch (error) {
      console.error('Error:', error);
      setStatus('ready');
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : 'Connection failed',
        variant: "destructive",
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const endConversation = async () => {
    await chatRef.current?.disconnect();
    setIsConnected(false);
    setIsSpeaking(false);
    setIsSending(false);
    setStatus('ready');
  };

  const handleClose = () => {
    if (isConnected) {
      endConversation();
    }
    // Allow closing in all modes; in embedded panel mode the host script will also hide the iframe.
    setIsOpen(false);
    setMessages([]);
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || !isConnected) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsSending(true);

    try {
      chatRef.current?.sendText(userMessage);
    } catch (error) {
      console.error('Error sending message:', error);
      setIsSending(false);
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive",
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  useEffect(() => {
    return () => {
      chatRef.current?.disconnect();
    };
  }, []);

  const positionClasses = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
  };

  const postWidgetState = (nextOpen: boolean) => {
    if (!hasParent) return;
    const action = nextOpen ? 'widget-open' : 'widget-close';
    // Backwards-compatible (string) + forwards-compatible (object)
    window.parent.postMessage(action, '*');
    window.parent.postMessage({ type: 'lovable-embed-widget', action }, '*');
  };

  const statusText = {
    ready: 'Tap to start',
    connecting: 'Connecting...',
    listening: 'Listening...',
    speaking: 'Speaking...',
  };

  // Notify parent iframe about open/close state
  useEffect(() => {
    // In the production embed strategy we use separate launcher/panel iframes.
    // Avoid auto-posting on mount in those modes (it can force-open unexpectedly).
    if (mode !== 'single') return;
    postWidgetState(isOpen);
  }, [isOpen, mode]);

  // Signal to parent that widget is ready (content loaded)
  useEffect(() => {
    if (tokenValidated && configLoaded && hasParent) {
      window.parent.postMessage('widget-ready', '*');
      window.parent.postMessage({ type: 'lovable-embed-widget', action: 'widget-ready' }, '*');
    }
  }, [tokenValidated, configLoaded, hasParent]);

  // Show auth error state
  if (authError) {
    return (
      <div className="w-full h-full flex items-center justify-center p-4" style={{ backgroundColor: 'transparent' }}>
        <div 
          className="text-center p-4 rounded-lg"
          style={{ backgroundColor: '#1A1A1A', color: '#FF6B6B' }}
        >
          <p className="text-sm">Widget unavailable</p>
          <p className="text-xs text-[#666] mt-1">{authError}</p>
        </div>
      </div>
    );
  }

  // Wait for token validation and config to load
  if (!tokenValidated || !configLoaded) {
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'transparent' }}>
        <div className="animate-pulse text-[#666] text-sm">Loading...</div>
      </div>
    );
  }

  const showTabs = communicationMode === 'both';
  const showVoice = communicationMode === 'voice' || (communicationMode === 'both' && activeTab === 'voice');
  const showChat = communicationMode === 'chat' || (communicationMode === 'both' && activeTab === 'chat');
  const isTextOnlyActive = communicationMode === 'chat' || (communicationMode === 'both' && activeTab === 'chat');

  const defaultTitle = communicationMode === 'chat' ? 'Chat Assistant' : communicationMode === 'both' ? 'Assistant' : 'Voice Assistant';
  const displayTitle = titleParam || defaultTitle;

  const headerStatus = !isConnected
    ? 'Tap to start'
    : isTextOnlyActive
      ? (isSending ? 'Thinking...' : 'Ready')
      : statusText[status];

  // Standalone preview behavior:
  // - /embed/widget?mode=panel should appear in the requested corner (not top-right)
  // - Close (X) should hide the panel and show a launcher-like button
  const renderLauncher =
    (mode === 'single' && !isOpen) ||
    isLauncherMode ||
    (isPanelMode && !hasParent && !isOpen);

  const renderPanel =
    (mode === 'single' && isOpen) ||
    (isPanelMode && (hasParent || isOpen));
  console.log(isPanelMode, 'isPanelModeisPanelMode')
  return (
    <div className="reservation-main">
    <div className="w-full h-full" style={{ backgroundColor: 'transparent' }}>
      <Toaster />

      {/* Floating Button (launcher) */}
      {renderLauncher && (
        <div
          className={cn(
            // In embedded/iframe usage we center the launcher within the iframe.
            // In standalone panel-preview, we render the launcher as a fixed element in the chosen corner.
            isPanelMode && !hasParent
              ? cn('fixed z-[2147483647]', positionClasses[position as keyof typeof positionClasses] || positionClasses['bottom-right'])
              : 'w-full h-full flex items-center justify-center'
          )}
          style={isPanelMode && !hasParent ? { padding: 16 } : undefined}
        >
          <button
            onClick={() => {
              postWidgetState(true);
              setIsOpen(true);
            }}
            className="flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
            style={{ backgroundColor: accentColor }}
          >
            <MessageCircle className="h-6 w-6" style={{ color: '#0A0A0A' }} />
          </button>
        </div>
      )}

      {/* Widget Panel */}
      {renderPanel && (
        <div 
          className={cn(
            // In embedded usage the iframe itself is positioned by the host page script.
            // For standalone preview, we pin the panel to the viewport so position=bottom-right etc works.
            hasParent ? 'w-full h-full flex' : 'fixed inset-0 flex p-4',
            position === 'top-left' && "items-start justify-start",
            position === 'top-right' && "items-start justify-end",
            position === 'bottom-left' && "items-end justify-start",
            (position === 'bottom-right' || !position) && "items-end justify-end"
          )}
        >
          <div
            className="w-80 h-[480px] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            style={{ backgroundColor: '#0A0A0A', border: '1px solid #333' }}
          >
            {/* Header */}
            <header 
              className="flex items-center justify-between px-4 py-3"
              style={{ backgroundColor: '#1A1A1A' }}
            >
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: accentColor }}
              >
                {showVoice ? (
                  <Phone className="h-5 w-5" style={{ color: '#0A0A0A' }} />
                ) : (
                  <MessageCircle className="h-5 w-5" style={{ color: '#0A0A0A' }} />
                )}
              </div>
              <div>
                <h1 className="font-semibold text-sm text-white">{displayTitle}</h1>
                <p className="text-xs text-[#999]">
                  {isConnected ? statusText[status] : 'Tap to start'}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                // Prefer telling the host page to close (restores the DOM launcher).
                // In standalone preview there's no parent, so we just close locally.
                if (hasParent) postWidgetState(false);
                handleClose();
              }}
              className="p-2 rounded-full hover:bg-[#333] transition-colors cursor-pointer"
              style={{ zIndex: 10, position: 'relative' }}
              type="button"
              aria-label="Close"
            >
              <X className="h-5 w-5 text-[#999]" />
            </button>
            </header>

          {/* Tabs for "both" mode */}
          {showTabs && (
            <div className="flex border-b border-[#333]" style={{ backgroundColor: '#1A1A1A' }}>
              <button
                onClick={() => {
                  if (isConnected) endConversation();
                  setActiveTab('voice');
                }}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors",
                  activeTab === 'voice' 
                    ? "text-white border-b-2" 
                    : "text-[#666] hover:text-[#999]"
                )}
                style={{ borderColor: activeTab === 'voice' ? accentColor : 'transparent' }}
              >
                <Mic className="h-4 w-4" />
                Voice
              </button>
              <button
                onClick={() => {
                  if (isConnected) endConversation();
                  setActiveTab('chat');
                }}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors",
                  activeTab === 'chat' 
                    ? "text-white border-b-2" 
                    : "text-[#666] hover:text-[#999]"
                )}
                style={{ borderColor: activeTab === 'chat' ? accentColor : 'transparent' }}
              >
                <MessageCircle className="h-4 w-4" />
                Chat
              </button>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center text-[#666] text-sm py-8">
                {showVoice ? (
                  <>
                    <Phone 
                      className="h-12 w-12 mx-auto mb-3" 
                      style={{ color: accentColor, opacity: 0.5 }} 
                    />
                    <p>Click below to start</p>
                    <p className="text-xs mt-1">a voice conversation</p>
                  </>
                ) : (
                  <>
                    <MessageCircle 
                      className="h-12 w-12 mx-auto mb-3" 
                      style={{ color: accentColor, opacity: 0.5 }} 
                    />
                    <p>Click below to connect</p>
                    <p className="text-xs mt-1">then type your message</p>
                  </>
                )}
              </div>
            )}
            {messages.map((message, index) => (
              <div
                key={index}
                className={cn(
                  "flex",
                  message.role === 'user' ? "justify-end" : "justify-start"
                )}
              >
                <div
                  className={cn(
                    "rounded-2xl px-3 py-2 max-w-[85%] text-sm",
                    message.role === 'user' ? "rounded-br-md" : "rounded-bl-md"
                  )}
                  style={{
                    backgroundColor: message.role === 'user' ? accentColor : '#1A1A1A',
                    color: message.role === 'user' ? '#0A0A0A' : '#FAFAFA'
                  }}
                >
                  {message.content}
                </div>
              </div>
            ))}
            {isSending && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-md px-3 py-2 text-sm" style={{ backgroundColor: '#1A1A1A' }}>
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-[#666] animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 rounded-full bg-[#666] animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 rounded-full bg-[#666] animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Voice Control */}
          {showVoice && (
            <div 
              className="p-4 flex flex-col items-center gap-2"
              style={{ backgroundColor: '#1A1A1A' }}
            >
              {isConnected && (
                <div className="flex items-end gap-1 h-6 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="w-1 rounded-full transition-all duration-100"
                      style={{
                        backgroundColor: accentColor,
                        height: isSpeaking ? `${Math.random() * 20 + 4}px` : '4px',
                      }}
                    />
                  ))}
                </div>
              )}

              <button
                onClick={isConnected ? endConversation : startConversation}
                disabled={isConnecting}
                className={cn(
                  "relative flex items-center justify-center w-14 h-14 rounded-full transition-all",
                  isConnecting && "opacity-50"
                )}
                style={{
                  backgroundColor: isConnected ? accentColor : '#333',
                  boxShadow: isConnected ? `0 0 15px ${accentColor}66` : 'none'
                }}
              >
                {isConnected ? (
                  <PhoneOff className="h-5 w-5" style={{ color: '#0A0A0A' }} />
                ) : (
                  <Phone className="h-5 w-5 text-[#999]" />
                )}
                
                {isSpeaking && (
                  <div 
                    className="absolute inset-0 rounded-full animate-ping opacity-30"
                    style={{ backgroundColor: accentColor }}
                  />
                )}
              </button>
            </div>
          )}

          {/* Chat Control */}
          {showChat && (
            <div 
              className="p-3"
              style={{ backgroundColor: '#1A1A1A' }}
            >
              {!isConnected ? (
                <button
                  onClick={startConversation}
                  disabled={isConnecting}
                  className={cn(
                    "w-full py-3 rounded-lg font-medium text-sm transition-all",
                    isConnecting && "opacity-50"
                  )}
                  style={{ backgroundColor: accentColor, color: '#0A0A0A' }}
                >
                  {isConnecting ? 'Connecting...' : 'Start Chat'}
                </button>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type a message..."
                    className="flex-1 px-3 py-2 rounded-lg text-sm text-white placeholder-[#666] focus:outline-none focus:ring-1"
                    style={{ backgroundColor: '#333', borderColor: accentColor }}
                    disabled={isSending}
                  />
                  <button
                    onClick={sendChatMessage}
                    disabled={!chatInput.trim() || isSending}
                    className={cn(
                      "px-3 py-2 rounded-lg transition-all",
                      (!chatInput.trim() || isSending) && "opacity-50"
                    )}
                    style={{ backgroundColor: accentColor }}
                  >
                    <Send className="h-4 w-4" style={{ color: '#0A0A0A' }} />
                  </button>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default EmbedWidget;
